/**
 * Remembers whether silent authentication (checkSession, a hidden iframe with prompt=none)
 * is worth attempting in this browser, to avoid failing silent auth calls.
 *
 * - No hint: no known Auth0 session, silent auth would fail with login_required.
 * - 'session': the user has logged in from this browser, silent auth should work.
 * - 'blocked': silent auth failed even though the Auth0 session was alive, i.e. the browser
 *   blocks third-party cookies (Safari, Firefox, etc). The redirect login is used instead.
 */

const HINT_KEY = "extranet:silent-auth-hint";
const FAILED_AT_KEY = "extranet:silent-auth-failed-at";

/** Margin for clock differences between the browser and Auth0. */
const CLOCK_SKEW_MARGIN_MS = 5 * 60 * 1000;
/** Retry silent auth now and then, in case the browser settings change. */
const BLOCKED_TTL_MS = 7 * 24 * 60 * 60 * 1000;

type Hint = { state: "session" } | { state: "blocked"; until: number };

const read = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key: string, value: string | null) => {
  try {
    if (value == null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, value);
    }
  } catch {
    // Storage might be unavailable, the hint is only an optimization
  }
};

const getHint = (): Hint | null => {
  try {
    return JSON.parse(read(HINT_KEY) ?? "null") as Hint | null;
  } catch {
    return null;
  }
};

const setHint = (hint: Hint | null) =>
  write(HINT_KEY, hint ? JSON.stringify(hint) : null);

const isBlocked = (hint: Hint | null): boolean =>
  hint?.state === "blocked" && hint.until > Date.now();

export const shouldTrySilentAuth = (): boolean => {
  const hint = getHint();
  return (
    hint?.state === "session" || (hint?.state === "blocked" && !isBlocked(hint))
  );
};

/** Call when silent auth failed because there was no usable Auth0 session. */
export const markSilentAuthFailed = () => {
  if (!isBlocked(getHint())) {
    setHint(null);
  }
  write(FAILED_AT_KEY, String(Date.now()));
};

/** Call after a successful silent auth. */
export const markSilentAuthSucceeded = () => {
  write(FAILED_AT_KEY, null);
  setHint({ state: "session" });
};

/**
 * Call after a successful redirect login. If the Auth0 session used for the login was
 * created before silent auth failed, the session was alive but invisible to the iframe.
 *
 * @param authTime the auth_time claim of the ID token, in seconds
 */
export const markRedirectLoginSucceeded = (authTime: number | undefined) => {
  const failedAt = Number(read(FAILED_AT_KEY) ?? 0);
  write(FAILED_AT_KEY, null);

  if (
    failedAt &&
    authTime &&
    authTime * 1000 < failedAt - CLOCK_SKEW_MARGIN_MS
  ) {
    setHint({ state: "blocked", until: Date.now() + BLOCKED_TTL_MS });
  } else if (!isBlocked(getHint())) {
    setHint({ state: "session" });
  }
};
